// Modal "Créditos SMS" da tela /companies.
// Mockup: "Modal Créditos SMS + Confirmação de exclusão".
//
// O segmentado "Adicionar / Remover" do mockup entrou só como adição: a rota
// `POST /sms/admin/creditar` recusa quantidade <= 0 (CreditosSmsService), e
// `debitarSms` existe mas é o débito de envio real — usá-lo para estorno
// administrativo misturaria as duas coisas no extrato. Remover crédito
// precisa de rota própria.
//
// O campo "Motivo" não está no mockup, mas é obrigatório no backend e vai
// para o extrato; sem ele o lançamento não é rastreável.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import SmsIcon from "@material-ui/icons/Sms";
import { makeStyles, alpha } from "@material-ui/core/styles";

import AerosDialog from "../AerosDialog";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useDate } from "../../hooks/useDate";
import { formatBRL, novoSaldoSms, parseAmount } from "../../pages/Companies/companyRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  return {
    headerIcon: {
      width: 38,
      height: 38,
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      backgroundColor: alpha(AEROS.cyan, 0.14),
      color: stateText(theme, AEROS.cyan, "info"),
      "& .MuiSvgIcon-root": { fontSize: 20 },
    },
    packGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0,1fr))",
      gap: 8,
    },
    pack: {
      border: `1px solid ${border}`,
      backgroundColor: theme.palette.aeros.surface,
      borderRadius: 10,
      padding: "10px 8px",
      textAlign: "center",
      cursor: "pointer",
      font: "inherit",
      color: "inherit",
      minWidth: 0,
    },
    packActive: {
      borderColor: AEROS.cyan,
      backgroundColor: alpha(AEROS.cyan, 0.1),
    },
    packQty: {
      fontSize: 15,
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
    },
    packPrice: { fontSize: 10.5, color: theme.palette.text.secondary },
    resultRow: {
      display: "flex",
      alignItems: "center",
      fontSize: 12,
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.aeros.surface,
      borderRadius: 9,
      padding: "9px 11px",
    },
    resultValue: {
      marginLeft: "auto",
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      color: stateText(theme, AEROS.success, "success"),
    },
    extratoTitle: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      marginBottom: 8,
    },
    extratoBox: {
      border: `1px solid ${border}`,
      borderRadius: 10,
      maxHeight: 150,
      overflowY: "auto",
      ...theme.scrollbarStyles,
    },
    extratoRow: {
      display: "grid",
      gridTemplateColumns: "120px 70px 60px minmax(0,1fr)",
      gap: 8,
      padding: "7px 11px",
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      borderBottom: `1px solid ${border}`,
      alignItems: "center",
      "&:last-child": { borderBottom: "none" },
    },
    extratoQty: { fontVariantNumeric: "tabular-nums", textAlign: "right" },
    extratoReason: {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      minWidth: 0,
    },
    extratoEmpty: {
      padding: "12px 11px",
      fontSize: 12,
      color: theme.palette.aeros.textTertiary,
      textAlign: "center",
    },
  };
});

const SmsCreditosModal = ({ open, onClose, company }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const { datetimeToClient } = useDate();

  // A tela manda a empresa da listagem (`id`); a lista de saldos usa
  // `companyId`. Aceita as duas para o modal servir aos dois chamadores.
  const companyId = company?.id || company?.companyId;

  const [saldo, setSaldo] = useState(0);
  const [pacotes, setPacotes] = useState([]);
  const [extrato, setExtrato] = useState([]);
  const [quantidade, setQuantidade] = useState("");
  const [motivo, setMotivo] = useState("");
  const [saving, setSaving] = useState(false);

  const carregar = useCallback(async () => {
    if (!companyId) return;
    try {
      const [saldosResp, pacotesResp, extratoResp] = await Promise.all([
        api.get("/sms/admin/saldos").catch(() => ({ data: [] })),
        api.get("/sms/pacotes").catch(() => ({ data: [] })),
        api.get(`/sms/admin/extrato/${companyId}`).catch(() => ({ data: [] })),
      ]);
      const atual = (saldosResp.data || []).find(
        (s) => s.companyId === companyId
      );
      setSaldo(atual ? atual.smsSaldo : company?.smsSaldo || 0);
      setPacotes(Array.isArray(pacotesResp.data) ? pacotesResp.data : []);
      setExtrato(Array.isArray(extratoResp.data) ? extratoResp.data : []);
    } catch (err) {
      toastError(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (!open) return;
    setQuantidade("");
    setMotivo("");
    carregar();
  }, [open, carregar]);

  const novoSaldo = useMemo(
    () => novoSaldoSms(saldo, quantidade, "add"),
    [saldo, quantidade]
  );

  const handleClose = () => {
    setQuantidade("");
    setMotivo("");
    onClose();
  };

  const handleCreditar = async () => {
    const qtd = Number(quantidade);
    if (!qtd || qtd <= 0) {
      toast.error(i18n.t("aeros.companies.sms.invalidAmount"));
      return;
    }
    if (!motivo.trim()) {
      toast.error(i18n.t("aeros.companies.sms.reasonRequired"));
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/sms/admin/creditar", {
        companyId,
        quantidade: qtd,
        motivo: motivo.trim(),
      });
      setSaldo(data.saldo);
      toast.success(i18n.t("aeros.companies.sms.applied"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  const renderTipo = (tipo) =>
    i18n.t(
      String(tipo).toLowerCase() === "credito"
        ? "aeros.companies.sms.credit"
        : "aeros.companies.sms.debit"
    );

  return (
    <AerosDialog
      open={open}
      onClose={handleClose}
      width={520}
      title={`${i18n.t("aeros.companies.sms.title")}${
        company?.name ? ` · ${company.name}` : ""
      }`}
      subtitle={i18n.t("aeros.companies.sms.currentBalance", { count: saldo })}
      onSubmit={handleCreditar}
      submitLabel={i18n.t("aeros.companies.sms.apply")}
      loading={saving}
      headerIcon={
        <div className={classes.headerIcon}>
          <SmsIcon />
        </div>
      }
    >
      {pacotes.length > 0 && (
        <div className={classes.packGrid}>
          {pacotes.map((pacote) => (
            <button
              type="button"
              key={pacote.id}
              className={`${classes.pack} ${
                Number(quantidade) === pacote.quantidade ? classes.packActive : ""
              }`}
              onClick={() => setQuantidade(String(pacote.quantidade))}
              aria-pressed={Number(quantidade) === pacote.quantidade}
            >
              <div className={classes.packQty}>
                {pacote.quantidade.toLocaleString("pt-BR")}
              </div>
              <div className={classes.packPrice}>
                {formatBRL(parseAmount(pacote.valor), 0)}
              </div>
            </button>
          ))}
        </div>
      )}

      <div className={m.grid2}>
        <div>
          <label className={m.fieldLabel}>
            {i18n.t("aeros.companies.sms.customAmount")}
          </label>
          <TextField
            fullWidth
            type="number"
            variant="outlined"
            className={`${m.input} ${m.inputSmall}`}
            placeholder={i18n.t("aeros.companies.sms.amountPlaceholder")}
            value={quantidade}
            onChange={(e) => setQuantidade(e.target.value)}
            inputProps={{ min: 1 }}
          />
        </div>
        <div>
          <label className={m.fieldLabel}>
            {i18n.t("aeros.companies.sms.reason")} *
          </label>
          <TextField
            fullWidth
            variant="outlined"
            className={`${m.input} ${m.inputSmall}`}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
        </div>
      </div>

      <div className={classes.resultRow}>
        {i18n.t("aeros.companies.sms.newBalance")}
        <span className={classes.resultValue}>
          {novoSaldo.toLocaleString("pt-BR")} SMS
        </span>
      </div>

      <div>
        <div className={classes.extratoTitle}>
          {i18n.t("aeros.companies.sms.statement")}
        </div>
        <div className={classes.extratoBox}>
          {extrato.length === 0 ? (
            <div className={classes.extratoEmpty}>
              {i18n.t("aeros.companies.sms.noEntries")}
            </div>
          ) : (
            extrato.map((lancamento) => (
              <div className={classes.extratoRow} key={lancamento.id}>
                <span>{datetimeToClient(lancamento.createdAt)}</span>
                <span>{renderTipo(lancamento.tipo)}</span>
                <span className={classes.extratoQty}>{lancamento.quantidade}</span>
                <span className={classes.extratoReason} title={lancamento.motivo}>
                  {lancamento.motivo}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </AerosDialog>
  );
};

export default SmsCreditosModal;
